import * as ACTIONS from 'constants/action_types';
import * as SETTINGS from 'constants/settings';
import * as COLLECTIONS from 'constants/collections';
import * as SHARED_PREFERENCES from 'constants/shared_preferences';
import dayjs from 'util/dayjs';
import { getSubsetFromKeysArray } from 'util/sync-settings';
import { UNSYNCED_SETTINGS } from 'config';
const { CLIENT_SYNC_KEYS } = SHARED_PREFERENCES;
const settingsToIgnore = (UNSYNCED_SETTINGS && UNSYNCED_SETTINGS.trim().split(' ')) || [];
const clientSyncKeys = settingsToIgnore.length
  ? CLIENT_SYNC_KEYS.filter((k) => !settingsToIgnore.includes(k))
  : CLIENT_SYNC_KEYS;
const reducers = {};
const defaultState = {
  isNight: false,
  findingFFmpeg: false,
  loadedLanguages: window.i18n_messages ? [...Object.keys(window.i18n_messages), 'en'] : ['en'],
  customWalletServers: [],
  sharedPreferences: {},
  daemonSettings: {},
  daemonStatus: {
    ffmpeg_status: {},
  },
  clientSettings: {
    // UX
    [SETTINGS.EMAIL_COLLECTION_ACKNOWLEDGED]: false,
    [SETTINGS.FOLLOWING_ACKNOWLEDGED]: false,
    [SETTINGS.TAGS_ACKNOWLEDGED]: false,
    [SETTINGS.FYP_MODAL_SHOWN]: false,
    [SETTINGS.ENABLE_SYNC]: IS_WEB,
    [SETTINGS.ENABLE_PUBLISH_PREVIEW]: true,
    [SETTINGS.ACTIVE_CHANNEL_CLAIM]: undefined,
    [SETTINGS.UPLOAD_PAGE_FILTERING]: {
      isFilteringEnabled: false,
      sortOption: {
        key: 'updatedAt',
        value: 'asc',
      },
    },
    // UI
    [SETTINGS.LANGUAGE]: null,
    [SETTINGS.AVAILABLE_LANGUAGES]: null,
    [SETTINGS.SEARCH_IN_LANGUAGE]: false,
    [SETTINGS.THEME]: 'system',
    [SETTINGS.THEMES]: ['dark', 'light', 'system'],
    [SETTINGS.HOMEPAGE]: null,
    [SETTINGS.HOMEPAGE_ORDER]: {
      active: null,
      hidden: null,
    },
    [SETTINGS.HOMEPAGE_ORDER_APPLY_TO_SIDEBAR]: false,
    [SETTINGS.HIDE_SPLASH_ANIMATION]: false,
    [SETTINGS.HIDE_BALANCE]: false,
    [SETTINGS.HIDE_TITLE_NOTIFICATION_COUNT]: false,
    [SETTINGS.OS_NOTIFICATIONS_ENABLED]: true,
    [SETTINGS.AUTOMATIC_DARK_MODE_ENABLED]: false,
    [SETTINGS.CLOCK_24H]: false,
    [SETTINGS.TILE_LAYOUT]: true,
    [SETTINGS.VIDEO_THEATER_MODE]: false,
    [SETTINGS.VIDEO_PLAYBACK_RATE]: 1,
    [SETTINGS.DESKTOP_WINDOW_ZOOM]: 1,
    [SETTINGS.DARK_MODE_TIMES]: {
      from: {
        hour: '21',
        min: '00',
        formattedTime: '21:00',
      },
      to: {
        hour: '8',
        min: '00',
        formattedTime: '8:00',
      },
    },
    // Purchasing
    [SETTINGS.INSTANT_PURCHASE_ENABLED]: false,
    [SETTINGS.INSTANT_PURCHASE_MAX]: {
      currency: 'LBC',
      amount: 0.1,
    },
    [SETTINGS.PREFERRED_CURRENCY]: 'USD',
    [SETTINGS.CRYPTO_DISCLAIMERS]: true,
    // Content
    [SETTINGS.SHOW_MATURE]: false,
    [SETTINGS.AUTOPLAY_MEDIA]: true,
    [SETTINGS.VIDEO_PREVIEWS]: true,
    [SETTINGS.HIDE_SHORTS]: false,
    [SETTINGS.HIDDEN_TAGS]: [],
    [SETTINGS.DISABLE_SHORTS_VIEW]: false,
    [SETTINGS.FLOATING_PLAYER]: true,
    [SETTINGS.AUTO_DOWNLOAD]: true,
    [SETTINGS.HIDE_MEMBERS_ONLY_CONTENT]: false,
    [SETTINGS.HIDE_REPOSTS]: false,
    [SETTINGS.HIDE_YOUTUBE_MIRRORS]: false,
    [SETTINGS.DEFAULT_COLLECTION_ACTION]: COLLECTIONS.DEFAULT_ACTION_VIEW,
    [SETTINGS.HIDE_SCHEDULED_LIVESTREAMS]: false,
    [SETTINGS.DEFAULT_VIDEO_QUALITY]: null,
    [SETTINGS.P2P_DELIVERY]: false,
    [SETTINGS.P2P_OPT_IN_DISMISSED]: false,
    // OS
    [SETTINGS.TO_TRAY_WHEN_CLOSED]: true,
  },
};
defaultState.clientSettings[SETTINGS.AUTOPLAY_NEXT] = defaultState.clientSettings[SETTINGS.AUTOPLAY_MEDIA];
defaultState.clientSettings[SETTINGS.AUTOPLAY_NEXT_SHORTS] = false;

reducers[ACTIONS.REHYDRATE] = (state, action) => {
  const { clientSettings } = state;

  if (action && action.payload && action.payload.settings) {
    const persistedSettings = action.payload && action.payload.settings;
    const persistedClientSettings = persistedSettings.clientSettings;
    const newClientSettings = { ...clientSettings, ...persistedClientSettings };
    return Object.assign({}, state, { ...persistedSettings, clientSettings: newClientSettings });
  }

  return Object.assign({}, state, {
    clientSettings,
  });
};

reducers[ACTIONS.FINDING_FFMPEG_STARTED] = (state) =>
  Object.assign({}, state, {
    findingFFmpeg: true,
  });

reducers[ACTIONS.FINDING_FFMPEG_COMPLETED] = (state) =>
  Object.assign({}, state, {
    findingFFmpeg: false,
  });

reducers[ACTIONS.DAEMON_SETTINGS_RECEIVED] = (state, action) =>
  Object.assign({}, state, {
    daemonSettings: action.data.settings,
  });

reducers[ACTIONS.DAEMON_STATUS_RECEIVED] = (state, action) =>
  Object.assign({}, state, {
    daemonStatus: action.data.status,
  });

reducers[ACTIONS.CLIENT_SETTING_CHANGED] = (state, action) => {
  const { key, value } = action.data;
  const clientSettings = Object.assign({}, state.clientSettings);
  clientSettings[key] = value;
  return Object.assign({}, state, {
    clientSettings,
  });
};

reducers[ACTIONS.UPDATE_IS_NIGHT] = (state) => {
  const { from, to } = state.clientSettings[SETTINGS.DARK_MODE_TIMES];
  const now = dayjs();
  const startNightMoment = dayjs(from.formattedTime, 'HH:mm');
  const endNightMoment = dayjs(to.formattedTime, 'HH:mm');
  const isNight = !(now.isAfter(endNightMoment) && now.isBefore(startNightMoment));
  return Object.assign({}, state, {
    isNight,
  });
};

reducers[ACTIONS.DOWNLOAD_LANGUAGE_SUCCESS] = (state, action) => {
  const { loadedLanguages } = state;
  const { language } = action.data;

  if (language && loadedLanguages && !loadedLanguages.includes(language)) {
    return Object.assign({}, state, {
      loadedLanguages: [...loadedLanguages, language],
    });
  } else {
    return state;
  }
};

reducers[ACTIONS.SHARED_PREFERENCE_SET] = (state, action) => {
  const { key, value } = action.data;
  const sharedPreferences = Object.assign({}, state.sharedPreferences);
  sharedPreferences[key] = value;
  return Object.assign({}, state, {
    sharedPreferences,
  });
};

reducers[ACTIONS.SYNC_CLIENT_SETTINGS] = (state) => {
  const { clientSettings } = state;
  const sharedPreferences = Object.assign({}, state.sharedPreferences);
  const selectedClientSettings = getSubsetFromKeysArray(clientSettings, clientSyncKeys);
  const newSharedPreferences = { ...sharedPreferences, ...selectedClientSettings };
  return Object.assign({}, state, {
    sharedPreferences: newSharedPreferences,
  });
};

reducers[ACTIONS.USER_STATE_POPULATE] = (state, action) => {
  const { clientSettings: currentClientSettings } = state;
  const { settings: sharedPreferences } = action.data;
  const selectedSettings = sharedPreferences ? getSubsetFromKeysArray(sharedPreferences, clientSyncKeys) : {};
  const mergedClientSettings = { ...currentClientSettings, ...selectedSettings };
  const newSharedPreferences = sharedPreferences || {};
  return Object.assign({}, state, {
    sharedPreferences: newSharedPreferences,
    clientSettings: mergedClientSettings,
  });
};

reducers[ACTIONS.SAVE_CUSTOM_WALLET_SERVERS] = (state, action) => {
  return Object.assign({}, state, {
    customWalletServers: action.data,
  });
};

export default function reducer(state = defaultState, action: any) {
  const handler = reducers[action.type];
  if (handler) return handler(state, action);
  return state;
}
